import React, { useState } from 'react';
import { Check, X, Loader, ArrowRight, Database, Server, Shield, Send, Cloud, CheckCircle, AlertCircle, RefreshCw, Coffee, Cake, Mail, Calendar } from 'lucide-react';
import AdminNav from './AdminNav';

interface FlowStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'success' | 'error';
  timestamp?: string;
  details?: string;
  icon: React.ReactNode;
}

interface TestResult {
  success: boolean;
  message: string;
  data?: any;
}

export default function CRUDFlowTest() {
  const [testData, setTestData] = useState({
    operation: 'birthday-card',
    recipientName: 'John Doe',
    recipientEmail: 'john@example.com',
    message: 'Happy Birthday! 🎂',
  });

  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [testResults, setTestResults] = useState<TestResult | null>(null);

  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([
    {
      id: 'client-init',
      title: '1. Client Initiates Request',
      description: 'User submits birthday card form',
      status: 'pending',
      icon: <Cake className="w-5 h-5" />
    },
    {
      id: 'client-validate',
      title: '2. Client-Side Validation',
      description: 'Validating recipient details and message',
      status: 'pending',
      icon: <Shield className="w-5 h-5" />
    },
    {
      id: 'api-request',
      title: '3. API Request Sent',
      description: 'POST request to /api/birthday/send-card',
      status: 'pending',
      icon: <ArrowRight className="w-5 h-5" />
    },
    {
      id: 'worker-receive',
      title: '4. API Worker Receives',
      description: 'birthday-coffee-api worker processes request',
      status: 'pending',
      icon: <Server className="w-5 h-5" />
    },
    {
      id: 'worker-auth',
      title: '5. Worker Authentication',
      description: 'Verifying API credentials and rate limits',
      status: 'pending',
      icon: <Shield className="w-5 h-5" />
    },
    {
      id: 'cf-api-call',
      title: '6. Cloudflare API Call',
      description: 'Storing birthday card data in KV/D1',
      status: 'pending',
      icon: <Cloud className="w-5 h-5" />
    },
    {
      id: 'd1-write',
      title: '7. D1 Database Write',
      description: 'Recording birthday card in database',
      status: 'pending',
      icon: <Database className="w-5 h-5" />
    },
    {
      id: 'email-queue',
      title: '8. Email Queue',
      description: 'Queueing birthday card for delivery',
      status: 'pending',
      icon: <Mail className="w-5 h-5" />
    },
    {
      id: 'schedule-delivery',
      title: '9. Schedule Delivery',
      description: 'Setting up delivery for birthday date',
      status: 'pending',
      icon: <Calendar className="w-5 h-5" />
    },
    {
      id: 'worker-response',
      title: '10. Worker Sends Response',
      description: 'API worker returns success response',
      status: 'pending',
      icon: <ArrowRight className="w-5 h-5" />
    },
    {
      id: 'client-receive',
      title: '11. Client Receives Response',
      description: 'Browser processes the response',
      status: 'pending',
      icon: <CheckCircle className="w-5 h-5" />
    },
    {
      id: 'ui-update',
      title: '12. UI State Update',
      description: 'React component shows success message',
      status: 'pending',
      icon: <RefreshCw className="w-5 h-5" />
    },
    {
      id: 'complete',
      title: '13. Flow Complete',
      description: 'Birthday card successfully scheduled',
      status: 'pending',
      icon: <Coffee className="w-5 h-5" />
    }
  ]);

  const updateStep = (stepId: string, updates: Partial<FlowStep>) => {
    setFlowSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, ...updates } : step
    ));
  };

  const resetSteps = () => {
    setFlowSteps(prev => prev.map(step => ({
      ...step,
      status: 'pending',
      timestamp: undefined,
      details: undefined
    })));
    setCurrentStep(-1);
    setTestResults(null);
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const runTest = async () => {
    setIsRunning(true);
    resetSteps();

    try {
      // Step 1: Client Initiates
      setCurrentStep(0);
      updateStep('client-init', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(500);
      updateStep('client-init', {
        status: 'success',
        details: 'Card form submitted, processing started'
      });

      // Step 2: Client Validation
      setCurrentStep(1);
      updateStep('client-validate', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(400);

      if (!testData.recipientName || !testData.recipientEmail) {
        updateStep('client-validate', {
          status: 'error',
          details: 'Missing required recipient information'
        });
        throw new Error('Validation failed');
      }

      updateStep('client-validate', {
        status: 'success',
        details: 'All fields valid'
      });

      // Step 3: API Request
      setCurrentStep(2);
      updateStep('api-request', {
        status: 'in-progress',
        timestamp: new Date().toISOString(),
        details: `Payload: { "recipient": "${testData.recipientName}", "email": "${testData.recipientEmail}" }`
      });
      await sleep(300);
      updateStep('api-request', {
        status: 'success',
        details: `POST /api/birthday/send-card`
      });

      // Step 4: Worker Receives
      setCurrentStep(3);
      updateStep('worker-receive', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(400);
      updateStep('worker-receive', {
        status: 'success',
        details: 'Request received by worker'
      });

      // Step 5: Worker Auth
      setCurrentStep(4);
      updateStep('worker-auth', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(500);
      updateStep('worker-auth', {
        status: 'success',
        details: 'Authentication successful'
      });

      // Step 6: Cloudflare API Call
      setCurrentStep(5);
      updateStep('cf-api-call', {
        status: 'in-progress',
        timestamp: new Date().toISOString(),
        details: 'Storing card data in Cloudflare KV'
      });
      await sleep(800);
      updateStep('cf-api-call', {
        status: 'success',
        details: `KV key: birthday_card_${Date.now()}`
      });

      // Step 7: D1 Write
      setCurrentStep(6);
      updateStep('d1-write', {
        status: 'in-progress',
        timestamp: new Date().toISOString(),
        details: 'INSERT INTO birthday_cards ...'
      });
      await sleep(600);
      updateStep('d1-write', {
        status: 'success',
        details: 'Card record created, ID: ' + Math.random().toString(36).substr(2, 9)
      });

      // Step 8: Email Queue
      setCurrentStep(7);
      updateStep('email-queue', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(500);
      updateStep('email-queue', {
        status: 'success',
        details: 'Added to email delivery queue'
      });

      // Step 9: Schedule Delivery
      setCurrentStep(8);
      updateStep('schedule-delivery', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(600);
      const deliveryDate = new Date();
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      updateStep('schedule-delivery', {
        status: 'success',
        details: `Scheduled for: ${deliveryDate.toLocaleDateString()}`
      });

      // Step 10: Worker Response
      setCurrentStep(9);
      updateStep('worker-response', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(400);
      updateStep('worker-response', {
        status: 'success',
        details: 'HTTP 200 OK'
      });

      // Step 11: Client Receives
      setCurrentStep(10);
      updateStep('client-receive', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(300);
      updateStep('client-receive', {
        status: 'success',
        details: 'Response parsed successfully'
      });

      // Step 12: UI Update
      setCurrentStep(11);
      updateStep('ui-update', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(500);

      setTestResults({
        success: true,
        message: 'Birthday card successfully created and scheduled!',
        data: {
          recipient: testData.recipientName,
          email: testData.recipientEmail,
          cardId: 'card_' + Math.random().toString(36).substr(2, 9),
          scheduledDelivery: new Date(Date.now() + 86400000).toISOString(),
          status: 'scheduled'
        }
      });

      updateStep('ui-update', {
        status: 'success',
        details: 'Success message displayed'
      });

      // Step 13: Complete
      setCurrentStep(12);
      updateStep('complete', {
        status: 'in-progress',
        timestamp: new Date().toISOString()
      });
      await sleep(400);
      updateStep('complete', {
        status: 'success',
        details: 'Full cycle completed successfully! ☕'
      });

    } catch (error) {
      const failedStep = flowSteps[currentStep];
      if (failedStep) {
        updateStep(failedStep.id, {
          status: 'error',
          details: error instanceof Error ? error.message : 'Unknown error'
        });
      }

      setTestResults({
        success: false,
        message: 'Test failed',
        data: error
      });
    } finally {
      setIsRunning(false);
    }
  };

  const getStepIcon = (step: FlowStep) => {
    switch (step.status) {
      case 'pending':
        return <div className="text-gray-400">{step.icon}</div>;
      case 'in-progress':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'success':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'error':
        return <X className="w-5 h-5 text-red-500" />;
    }
  };

  const getStepColor = (step: FlowStep) => {
    switch (step.status) {
      case 'pending':
        return 'border-gray-300 bg-gray-50';
      case 'in-progress':
        return 'border-blue-400 bg-blue-50 animate-pulse';
      case 'success':
        return 'border-green-400 bg-green-50';
      case 'error':
        return 'border-red-400 bg-red-50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <AdminNav
        title="CRUD Flow Test"
        description="Watch how birthday cards flow through our system"
      />
      <div className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl p-8">
            {/* Test Configuration */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 mb-8 border border-amber-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Cake className="w-6 h-6 text-amber-600" />
              Test Birthday Card
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm mb-1">Operation Type</label>
                <select
                  value={testData.operation}
                  onChange={(e) => setTestData({...testData, operation: e.target.value})}
                  className="w-full px-4 py-2 bg-white border border-amber-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  disabled={isRunning}
                >
                  <option value="birthday-card">Send Birthday Card</option>
                  <option value="schedule-card">Schedule Card</option>
                  <option value="coffee-gift">Send Coffee Gift</option>
                  <option value="bulk-import">Bulk Import Birthdays</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={testData.recipientName}
                  onChange={(e) => setTestData({...testData, recipientName: e.target.value})}
                  className="w-full px-4 py-2 bg-white border border-amber-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  disabled={isRunning}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={testData.recipientEmail}
                  onChange={(e) => setTestData({...testData, recipientEmail: e.target.value})}
                  className="w-full px-4 py-2 bg-white border border-amber-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  disabled={isRunning}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm mb-1">Message</label>
                <input
                  type="text"
                  value={testData.message}
                  onChange={(e) => setTestData({...testData, message: e.target.value})}
                  className="w-full px-4 py-2 bg-white border border-amber-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  disabled={isRunning}
                  placeholder="Happy Birthday!"
                />
              </div>
            </div>
            <button
              onClick={runTest}
              disabled={isRunning}
              className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold px-6 py-3 rounded-lg transition-all flex items-center gap-2 shadow-lg"
            >
              {isRunning ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Running Test...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Run CRUD Test
                </>
              )}
            </button>
          </div>

          {/* Flow Steps */}
          <div className="space-y-3">
            {flowSteps.map((step, index) => (
              <div
                key={step.id}
                className={`relative flex items-start gap-4 p-4 rounded-lg border-2 transition-all ${getStepColor(step)}`}
              >
                {/* Step Number & Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center shadow-sm">
                    {getStepIcon(step)}
                  </div>
                </div>

                {/* Step Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">{step.title}</h3>
                    {step.status === 'in-progress' && (
                      <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full animate-pulse">
                        PROCESSING
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm mt-1">{step.description}</p>

                  {step.details && (
                    <div className="mt-2 p-2 bg-black/5 rounded text-xs font-mono text-gray-800">
                      {step.details}
                    </div>
                  )}

                  {step.timestamp && (
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(step.timestamp).toLocaleTimeString('en-US', {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}.{new Date(step.timestamp).getMilliseconds().toString().padStart(3, '0')}
                    </p>
                  )}
                </div>

                {/* Connection Line */}
                {index < flowSteps.length - 1 && (
                  <div className={`absolute left-6 top-14 w-0.5 h-8 ${
                    step.status === 'success' ? 'bg-green-300' :
                    step.status === 'error' ? 'bg-red-300' :
                    'bg-gray-300'
                  }`}></div>
                )}
              </div>
            ))}
          </div>

          {/* Test Results */}
          {testResults && (
            <div className={`mt-8 p-6 rounded-xl border-2 ${
              testResults.success
                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-400'
                : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-400'
            }`}>
              <div className="flex items-start gap-3">
                {testResults.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">Test Results</h3>
                  <p className="text-gray-700">{testResults.message}</p>
                  {testResults.data && (
                    <pre className="mt-3 p-3 bg-white/70 rounded text-xs text-gray-700 overflow-auto border border-gray-200">
                      {JSON.stringify(testResults.data, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <h3 className="text-gray-900 font-semibold mb-3">System Architecture</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="text-gray-700">
                <strong className="text-gray-900">Frontend (React):</strong> Steps 1-3, 11-13
              </div>
              <div className="text-gray-700">
                <strong className="text-gray-900">API Worker:</strong> Steps 4-6, 10
              </div>
              <div className="text-gray-700">
                <strong className="text-gray-900">Data & Services:</strong> Steps 7-9 (D1, Email, Scheduler)
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-600">
              This visualization demonstrates the complete data flow when sending a birthday card through the Birthday Coffee platform.
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}